import React, { useState, useEffect } from 'react';
import { api } from '../App';

// All applicable key routing stages for one TAS (done + pending). The estimated date is
// read-only (always derived — manual date, else order date + master days); the user can
// only enter or change the ACTUAL date, which writes back to the ERP via /stages/update.
// Shown on every critical TAS (the endpoint returns rows only when a pending stage is
// overdue). Renders nothing otherwise.
function fmtDate(d) {
  if (!d) return '';
  const x = new Date(d);
  return isNaN(x) ? '' : x.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function CriticalStagesBanner({ tasNo, orderType = 'garment', onSaved }) {
  const [stages, setStages] = useState(null);
  const [dates, setDates] = useState({});   // uniqueid -> YYYY-MM-DD (edited actual date)
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!tasNo) { setStages([]); return; }
    let alive = true;
    api(`/orders/tas-critical-stages/${encodeURIComponent(tasNo)}`)
      .then(d => { if (alive) setStages(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setStages([]); });
    return () => { alive = false; };
  }, [tasNo]);

  async function save(c) {
    const val = dates[c.uniqueid] ?? c.actual_iso;   // edited value, else its current actual
    if (!val) return;
    const [yyyy, mm, dd] = val.split('-');
    setSavingId(c.uniqueid);
    try {
      const res = await api('/stages/update', {
        method: 'POST',
        body: JSON.stringify({ uniqueid: c.uniqueid, actual_date: `${dd}/${mm}/${yyyy}`, remark: '', source: orderType })
      });
      if (res && (res.success || res.message)) {
        // mark this stage done in place (est date is untouched — it stays derived)
        setStages(prev => (prev || []).map(x => x.uniqueid === c.uniqueid
          ? { ...x, done: 1, actual_date: fmtDate(val), actual_iso: val } : x));
        setDates(p => { const n = { ...p }; delete n[c.uniqueid]; return n; });
        if (typeof onSaved === 'function') onSaved(c, val);
      }
    } finally { setSavingId(null); }
  }

  if (!stages || stages.length === 0) return null;
  const overdueCount = stages.filter(s => !s.done && s.days_over > 0).length;

  return (
    <div style={{ margin: '0 16px 12px', padding: '10px 14px', background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 10 }}>
      <div style={{ fontSize: 10, color: 'var(--red)', letterSpacing: 1.2, fontWeight: 700, marginBottom: 8 }}>
        ⚠ KEY ROUTING STAGES{overdueCount ? ` — ${overdueCount} OVERDUE` : ''}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {stages.map((c, i) => {
          const dVal = dates[c.uniqueid] ?? c.actual_iso ?? '';
          const savingThis = savingId === c.uniqueid;
          const overdue = !c.done && c.days_over > 0;
          return (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderTop: i ? '1px solid rgba(200,60,70,0.18)' : 'none' }}>
              {/* stage + est date (read-only) */}
              <div style={{ flex: '1 1 150px', minWidth: 140 }}>
                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 12 }}>{c.stage}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>est {c.est_date}{c.is_computed ? ' (from days)' : ''}</div>
              </div>
              {/* status */}
              <span style={{ fontWeight: 700, fontSize: 12, minWidth: 92, textAlign: 'right', color: c.done ? 'var(--green)' : overdue ? 'var(--red)' : 'var(--text3)' }}>
                {c.done ? '✓ done' : overdue ? `${c.days_over}d overdue` : 'upcoming'}
              </span>
              {/* actual date — the only editable field */}
              {c.uniqueid != null && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input type="date" value={dVal} max={todayISO()}
                    onChange={e => setDates(p => ({ ...p, [c.uniqueid]: e.target.value }))}
                    style={{ boxSizing: 'border-box', padding: '7px 9px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, colorScheme: 'light dark' }} />
                  {dVal && <span style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtDate(dVal)}</span>}
                  <button disabled={savingThis || !dVal} onClick={() => save(c)}
                    style={{ background: c.done ? 'var(--gold)' : 'var(--green)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 11, fontWeight: 600, cursor: (savingThis || !dVal) ? 'default' : 'pointer', opacity: (savingThis || !dVal) ? 0.55 : 1, whiteSpace: 'nowrap' }}>
                    {savingThis ? 'Saving…' : c.done ? 'Update' : 'Save actual'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
